import { Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { sharedLegendProps } from './shared/chartProps';
import { generateColors } from 'utils/colorUtils';
import { useMemo } from 'react';

const RADIAN = Math.PI / 180;

export interface ServiceData {
  name: string;
  value: number;
  percentage: string | number;
  color?: string;
}

interface ServicePieChartProps {
  data: ServiceData[];
  colorMap: Record<string, string>;
  tooltipFormatter?: (data: any) => React.ReactNode;
  height?: number;
  title?: string;
  valueUnit?: string;
}

const ServicePieChart = ({
  data,
  colorMap,
  tooltipFormatter,
  height = 380,
  title,
  valueUnit = '',
}: ServicePieChartProps) => {
  // Create a fallback color array based on the number of data points
  const fallbackColors = useMemo(() => generateColors(data.length), [data.length]);

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    value,
    name,
    payload,
    index,
  }: any) => {
    const radius = outerRadius * 1.1;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const textAnchor = x > cx ? 'start' : 'end';

    const color = payload.color || colorMap[name.toLowerCase()] || fallbackColors[index] || '#CCCCCC';

    return (
      <text x={x} y={y} fill={color} textAnchor={textAnchor} dominantBaseline="central" className="text-xs">
        {`${name}: ${value.toLocaleString()} ${valueUnit} (${payload.percentage}%)`}
      </text>
    );
  };

  const defaultTooltipContent = (props: any) => {
    if (!props.payload?.length) return null;
    const data = props.payload[0];
    const color = data.payload.color || data.payload.fill || fallbackColors[data.payload.index];

    return (
      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
        <div className="flex items-center gap-2 text-sm">
          <div className="h-3 w-3 rounded-full" style={{ backgroundColor: color }}></div>
          <span>
            {data.name}: {data.value.toLocaleString()} {valueUnit} ({data.payload.percentage}%)
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      {title && <h3 className="mb-6 text-base font-medium text-gray-700">{title}</h3>}
      <div style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={80}
              outerRadius={120}
              paddingAngle={2}
              dataKey="value"
              labelLine={false}
              label={renderCustomizedLabel}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color || colorMap[entry.name.toLowerCase()] || fallbackColors[index]}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              content={tooltipFormatter || defaultTooltipContent}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                padding: '8px 12px',
              }}
            />
            <Legend
              {...sharedLegendProps}
              formatter={(value, entry: any) => `${value} (${entry.payload.percentage}%)`}
              wrapperStyle={{
                paddingTop: '20px',
                fontSize: '12px',
                color: '#6B7280',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ServicePieChart;
